import React, { lazy, Suspense } from "react";
import { Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../contexts/useAppContext";
import { findBranchBySlug, buildBranchesPath } from "../../../utils/navigation";
import { getBranches, getCompanyData } from "../../../service";
import { DynamicHead } from "../../atoms/DynamicHead";
import { OnlineOrderingBanner } from "../../atoms/online-ordering-banner/OnlineOrderingBanner";

const MenuPage = lazy(() => import("../menu-page/MenuPage").then(m => ({ default: m.MenuPage })));
const CartPage = lazy(() => import("../cart-page/CartPage").then(m => ({ default: m.CartPage })));
const DeliveryPage = lazy(() => import("../delivery-page/DeliveryPage").then(m => ({ default: m.DeliveryPage })));
const SummaryPage = lazy(() => import("../summary-page/SummaryPage").then(m => ({ default: m.SummaryPage })));
const ThankYouPage = lazy(() => import("../thank-you/ThankYouPage").then(m => ({ default: m.ThankYouPage })));
const ToPayPage = lazy(() => import("../to-pay/ToPayPage").then(m => ({ default: m.ToPayPage })));
const StatutePage = lazy(() => import("../statute-page/StatutePage").then(m => ({ default: m.StatutePage })));
const ServicesPage = lazy(() => import("../services-page/ServicesPage").then(m => ({ default: m.ServicesPage })));
const SelectResource = lazy(() => import("../select-resource-page/SelectResource").then(m => ({ default: m.SelectResource })));
const ServiceDataPicker = lazy(() => import("../service-data-pickes-page/ServiceDataPicker").then(m => ({ default: m.ServiceDataPicker })));
const OrderDataPicker = lazy(() => import("../order-data-pickes-page/OrderDataPicker").then(m => ({ default: m.OrderDataPicker })));
const VariantSelect = lazy(() => import("../variant-select-page/VariantSelect").then(m => ({ default: m.VariantSelect })));
const WebsitePolicy = lazy(() => import("../website-policy/WebsitePolicy").then(m => ({ default: m.WebsitePolicy })));

const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <Spin size="large" />
  </div>
);

export const MenuFlowPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurantSlug: urlSlug, branchSlug } = useParams();
  const { currentPage, selectedBranch, setSelectedBranch, restaurantSlug } = useAppContext();
  const { data: companyData } = useQuery(["/company-data"], getCompanyData, { staleTime: 5 * 60 * 1000 });

  // Need to resolve branch from URL: either by branchSlug or by fetching all branches (single-branch case)
  const needsBranchResolution = !selectedBranch;

  const { isLoading: isBranchLoading } = useQuery(
    ["/branches-resolve", restaurantSlug ?? urlSlug, branchSlug],
    getBranches,
    {
      enabled: needsBranchResolution,
      onSuccess(data) {
        if (branchSlug) {
          // Multi-branch: resolve by slug
          const matched = findBranchBySlug(data, branchSlug);
          if (matched) {
            setSelectedBranch(matched);
          } else {
            navigate(buildBranchesPath(urlSlug ?? restaurantSlug), { replace: true });
          }
        } else if (data.length === 1) {
          // Single-branch: auto-select the only branch
          setSelectedBranch(data[0]);
        } else {
          // Multiple branches but no branchSlug in URL — go to branch picker
          navigate(buildBranchesPath(urlSlug ?? restaurantSlug), { replace: true });
        }
      },
      onError() {
        navigate(buildBranchesPath(urlSlug ?? restaurantSlug), { replace: true });
      },
    }
  );

  if (needsBranchResolution || isBranchLoading) return <PageLoader />;

  if (companyData && companyData.isActive === false) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '40px 20px',
        textAlign: 'center',
        background: 'var(--color-background-secondary)',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '24px' }}>🚫</div>
        <h2 style={{ fontSize: 'var(--font-size-heading2)', fontWeight: 'var(--font-weight-semibold)', marginBottom: '12px', fontFamily: 'Lato' }}>
          {t("menuFlowPage.pageTemporarilyUnavailable")}
        </h2>
        <p style={{ fontSize: 'var(--font-size-body-medium)', color: 'var(--color-text-secondary)', maxWidth: '400px' }}>
          {t("menuFlowPage.pageUnavailableDescription")}
        </p>
      </div>
    );
  }

  if (!selectedBranch) {
    navigate(buildBranchesPath(urlSlug ?? restaurantSlug), { replace: true });
    return <PageLoader />;
  }

  const renderPage = () => {
    switch (currentPage.page) {
      case "dishes": return <MenuPage />;
      case "cart": return <CartPage />;
      case "delivery": return <DeliveryPage />;
      case "delivery-date": return <OrderDataPicker />;
      case "summary": return <SummaryPage />;
      case "thank-you": return <ThankYouPage />;
      case "to-pay": return <ToPayPage />;
      case "statute": return <StatutePage />;
      case "services": return <ServicesPage />;
      case "resources": return <SelectResource />;
      case "reservate": return <ServiceDataPicker />;
      case "variants": return <VariantSelect />;
      case "website-policy": return <WebsitePolicy />;
      default: return <MenuPage />;
    }
  };

  const hideBannerPages: string[] = ["thank-you", "to-pay", "statute", "website-policy"];
  const showBanner = !hideBannerPages.includes(currentPage.page);

  return (
    <Suspense fallback={<PageLoader />}>
      <DynamicHead
        ogTitle={selectedBranch?.ogTitle}
        ogDescription={selectedBranch?.ogDescription}
        ogImage={selectedBranch?.ogImage}
        companyOgTitle={companyData?.ogTitle}
        companyOgDescription={companyData?.ogDescription}
        companyOgImage={companyData?.ogImage}
      />
      {showBanner && selectedBranch?.hasAgreement && (
        <OnlineOrderingBanner branch={selectedBranch} />
      )}
      {renderPage()}
    </Suspense>
  );
};
